import { ReactComponent as DefaultDoctorImage } from '@assets/icons/doctor_avatar_default.svg';
import Bar from '@components/Bar';
import Dot from '@components/Dot';
import FocusableBackButton from '@components/FocusableBackButton';
import FocusableButton from '@components/FocusableButton';
import FocusableIconButton from '@components/FocusableIconButton';
import { Gray, Green } from '@constants/color';
import { useBackHandlerContext } from '@context/BackHandler';
import { useDoctorSelectionContext } from '@context/DoctorSelection';
import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { motion } from 'framer-motion';
import { Fragment, useEffect, useLayoutEffect, useState } from 'react';

import { DOCTOR_DETAIL_BACK, DOCTOR_DETAIL_BUTTON } from './focusKey';

import * as styles from './styles';

const DoctorDetailView = () => {
  const { disableBack, addCallback } = useBackHandlerContext();

  const {
    selectedDoctorIndex,
    doctor,
    untactDepartmentsText,
    hospitalOperationTimeText,
    setItemFocus,
    setButtonFocus,
    goPatient,
    goDoctorSelection
  } = useDoctorSelectionContext();

  useLayoutEffect(() => {
    return addCallback(() => {
      goDoctorSelection();
      return false;
    });
  }, [addCallback, disableBack, goDoctorSelection]);

  useEffect(() => {
    setButtonFocus();
    return () => setItemFocus();
  }, [setButtonFocus, setItemFocus]);

  const [isLoaded, setIsLoaded] = useState(false);
  const onLoad = () => setIsLoaded(true);

  const { focusKey } = useFocusable();

  return (
    <FocusContext.Provider value={focusKey}>
      <div css={styles.container}>
        <div css={styles.mainContainer}>
          <FocusableBackButton
            focusKey={DOCTOR_DETAIL_BACK}
            onEnterPress={goDoctorSelection}
          />
          <div css={styles.mainContentContainer}>
            <motion.div
              layoutId={`doctor-image-${selectedDoctorIndex}`}
              css={styles.profileImageContainer}
            >
              <img
                css={[styles.image, isLoaded && styles.imageLoaded]}
                src={doctor?.tvImageUrl ?? ''}
                height={400}
                alt={doctor?.name}
                onLoad={onLoad}
              />
              {!isLoaded && <DefaultDoctorImage css={styles.defaultImage} />}
            </motion.div>
            <div css={styles.profileContainer}>
              <div css={styles.profileMainContainer}>
                <div css={styles.name}>{doctor?.name}</div>
              </div>
              <div css={styles.profileDescriptionContainer}>
                <div css={styles.profileDescription}>
                  <div css={styles.descriptionTitle}>진료 항목</div>
                  <div css={styles.descriptionContentContainer}>
                    {untactDepartmentsText}
                  </div>
                </div>
                <div css={styles.profileDescription}>
                  <div css={styles.descriptionTitle}>병원 정보</div>
                  <div css={styles.descriptionContentContainer}>
                    {doctor?.hospital.name}
                    <Bar width="1px" height="24px" color={Gray.Gray60} />
                    {doctor?.hospital.phone}
                  </div>
                </div>
                <div css={styles.profileDescription}>
                  <div css={styles.descriptionTitle}>진료 시간</div>
                  <div css={styles.descriptionContentContainer}>
                    {/* FIXME: input data */}
                    {hospitalOperationTimeText}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div css={styles.rightContainer}>
          {doctor && doctor?.untactOnTreatment ? (
            <Fragment>
              <FocusableIconButton
                focusKey={DOCTOR_DETAIL_BUTTON}
                onEnterPress={goPatient}
                iconType="arrow"
              >
                비대면 진료 신청하기
              </FocusableIconButton>
              <div css={styles.informationContainer}>
                <Dot color={Green.Green80} />
                <div css={styles.information}>비대면 진료비가 발생합니다.</div>
              </div>
            </Fragment>
          ) : (
            <FocusableButton focusable={false}>
              현재 자리를 비운 상태입니다
            </FocusableButton>
          )}
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export default DoctorDetailView;
